import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { HomeProduct, HomeProductForm, HomeProductSearchForm } from 'entities/home_product'

type HomeProductsResponse = PagingResponse & {
    results: HomeProduct[]
}

export function useFetchHomeProductsApi(searchForm?: Form<HomeProductSearchForm>): IndexApiSet<HomeProductsResponse> & { execute: () => void } {
    const apiPath = 'home_products/'
    const api = useIndexApi<HomeProductsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchHomeProductApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `home_products/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostHomeProductApi(): ApiSet<BaseResponse> & { execute: (form: Form<HomeProductForm>) => void } {
    const apiSet = usePostApi<BaseResponse, HomeProductForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<HomeProductForm>) => {
        const apiPath = `home_products/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutHomeProductApi(): ApiSet<BaseResponse> & { execute: (form: Form<HomeProductForm>) => void } {
    const apiSet = usePutApi<BaseResponse, HomeProductForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<HomeProductForm>) => {
        const apiPath = `home_products/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutHomeProductsApi(): ApiSet<BaseResponse> & { execute: (form: Form<HomeProductForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, HomeProductForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<HomeProductForm[]>) => {
        const apiPath = `home_products/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteHomeProductApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `home_products/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
